<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import PublicSiteFooter from '@/components/publicSite/PublicSiteFooter.vue'
import PublicSiteHeader from '@/components/publicSite/PublicSiteHeader.vue'

definePage({
  meta: {
    layout: 'blank',
    public: true,
  },
})

const route = useRoute()
const type = route.params.type as string
const content = ref('')
const isLoading = ref(true)
const { t, locale } = useI18n({ useScope: 'global' })

const titleKey = computed(() => `publicSite.legal.titles.${type}`)
const navTypeKey = computed(() => `publicSite.legal.nav.${type}`)
const localizedTitle = computed(() => {
  const translated = t(titleKey.value)
  return translated === titleKey.value ? t('publicSite.legal.titles.fallback') : translated
})

const localizedDate = computed(() =>
  new Intl.DateTimeFormat(locale.value || 'it', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(new Date()),
)

const fetchLegal = async () => {
  try {
    const { data } = await useApi<any>('/public/platform/legal', {
      headers: {
        'X-Locale': locale.value,
        'Accept-Language': locale.value,
      },
    })
    if (data.value.success) {
      const key = `platform_${type === 'terms' ? 'terms_conditions' : (type === 'privacy' ? 'privacy_policy' : 'cookie_policy')}`
      content.value = data.value.settings[key] || `<p class="text-center">${t('publicSite.legal.platformContentUnavailable')}</p>`
    }
  } catch (e) {
    content.value = `<p class="text-center text-error">${t('publicSite.legal.platformLoadingError')}</p>`
  } finally {
    isLoading.value = false
  }
}

onMounted(() => {
  fetchLegal()
})
watch(locale, fetchLegal)
</script>

<template>
  <div class="legal-premium-page">
    <PublicSiteHeader />

    <div class="legal-content-wrapper">
      <div class="container py-16">
        <VCard class="legal-card animate-fade-in">
          <div class="legal-header pa-8 pa-md-16">
            <h1 class="text-h1 font-weight-black gradient-text mb-4">{{ localizedTitle }}</h1>
            <p class="text-h5 opacity-60 font-weight-regular">
              {{ t('publicSite.shared.lastUpdated') }}: {{ localizedDate }}
            </p>
          </div>

          <VDivider class="border-white-opacity-10" />

          <div class="pa-8 pa-md-16">
            <div v-if="isLoading" class="text-center py-16">
              <VProgressCircular indeterminate color="primary" size="64" />
            </div>

            <div v-else class="legal-body-html" v-html="content"></div>
          </div>
        </VCard>
      </div>
    </div>

    <PublicSiteFooter />
  </div>
</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;600;800&display=swap');

.legal-premium-page {
  font-family: 'Outfit', sans-serif;
  background-color: #0F172A;
  color: #F1F5F9;
  min-height: 100vh;
}

.container {
  max-width: 1440px; /* Wider container like landing */
  margin: 0 auto;
  padding: 0 40px;
}

.legal-content-wrapper {
  padding-top: 40px;
}

.legal-card {
  background: rgba(30, 41, 59, 0.5) !important;
  backdrop-filter: blur(24px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 40px;
  box-shadow: 0 40px 80px -20px rgba(0, 0, 0, 0.4);
}

.gradient-text {
  background: linear-gradient(135deg, #818CF8 0%, #C084FC 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.legal-body-html {
  line-height: 2;
  font-size: 1.2rem;
  color: #CBD5E1;
  max-width: 1000px;
  margin: 0 auto;
}

.legal-body-html :deep(h2), .legal-body-html :deep(h3) {
  color: white;
  margin-top: 3rem;
  margin-bottom: 1.5rem;
  font-weight: 800;
}

.legal-body-html :deep(p) {
  margin-bottom: 1.8rem;
}

.legal-body-html :deep(ul) {
  margin-bottom: 1.8rem;
  padding-left: 2rem;
}

.legal-body-html :deep(li) {
  margin-bottom: 1rem;
}

.animate-fade-in {
  animation: fadeIn 0.8s ease-out forwards;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
}

@media (max-width: 960px) {
  .text-h1 { font-size: 3rem !important; }
}

@media (max-width: 600px) {
  .container { padding: 0 20px; }
  .text-h1 { font-size: 2.25rem !important; }
}
</style>
